import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import VolumetricBudgetMeter from '../../components/VolumetricBudgetMeter';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { downloadAuthedFile } from '../../utils/downloadAuthedFile';
import PipelineList from '../../components/shipping/PipelineList';
import PickupScheduler from '../../components/PickupScheduler';
// iter-235 SSOT — canonical shipment-state derivation used by every
// surface. Pack & Ship row actions read the matrix instead of deriving
// `isAwbCancelled` / `has_pickup` locally.
import { readShipmentState } from '../../lib/shipmentCtaMatrix';
import {
  Package,
  Truck,
  RefreshCw,
  Printer,
  Download,
  ExternalLink,
  MapPin,
  Weight,
  Ruler,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  Box,
  Calendar,
  FileText,
  Navigation,
  ClipboardList,
  ChevronRight,
  User,
  X,
} from 'lucide-react';

// Invoice Header Download Component
const InvoiceHeaderDownload = ({ apiCall }) => {
  const [loading, setLoading] = useState(false);
  const [invoiceHeaderUrl, setInvoiceHeaderUrl] = useState(null);
  
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await apiCall('/api/branding/');
        if (response?.invoice_header_url) {
          setInvoiceHeaderUrl(response.invoice_header_url);
        }
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    fetchBranding();
  }, [apiCall]);
  
  const handleDownload = async () => {
    if (!invoiceHeaderUrl) return;
    
    setLoading(true);
    try {
      // Open in new tab for download
      window.open(invoiceHeaderUrl, '_blank');
    } finally {
      setLoading(false);
    }
  };
  
  if (!invoiceHeaderUrl) return null;
  
  return (
    <Button 
      onClick={handleDownload} 
      variant="outline" 
      size="sm"
      disabled={loading}
      title="Download Invoice Header for your invoices"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <FileText className="h-4 w-4 mr-2" />
      )}
      Download Aniyor Invoice Header
    </Button>
  );
};

const PackAndShip = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const { formatPrice } = useCurrency();
  
  // iter-229d — Pending/Forward tabs are deprecated. Manifest is the
  // new default landing tab (orders with AWB ready for pickup scheduling).
  const [activeTab, setActiveTab] = useState('manifest');
  const [loading, setLoading] = useState(true);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [manifest, setManifest] = useState(null);
  const [selfDeliveryOrders, setSelfDeliveryOrders] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [selfTrackingInput, setSelfTrackingInput] = useState({});
  const [selfTrackingProvider, setSelfTrackingProvider] = useState({});
  
  // Pack & Ship Modal State
  const [packShipModalOpen, setPackShipModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [packingDetails, setPackingDetails] = useState({
    weight_kg: '',
    length_cm: '',
    breadth_cm: '',
    height_cm: '',
    insurance_opt_in: false
  });
  const [ratePreview, setRatePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [awbResult, setAwbResult] = useState(null); // Stores AWB after shipment creation
  const [labelDownloading, setLabelDownloading] = useState(false);
  const [savingDimensions, setSavingDimensions] = useState(false);
  
  // Tracking Dialog State
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [cancellingAwbId, setCancellingAwbId] = useState(null);
  
  // Pickup Locations State
  const [pickupLocations, setPickupLocations] = useState([]);
  const [pickupForm, setPickupForm] = useState({ name: '', address_line: '', city: '', state: '', pincode: '', phone: '', is_default: false });
  const [editingPickup, setEditingPickup] = useState(null);
  const [pickupSaving, setPickupSaving] = useState(false);
  
  // Pipeline summary (9-tab aggregate counts + filtered shipments per stage)
  const [pipeline, setPipeline] = useState({ counts: {}, totals: {} });

  // Order confirm state
  const [confirmingOrder, setConfirmingOrder] = useState({});
  const [confirmedOrders, setConfirmedOrders] = useState(new Set());

  // Manual AWB form state (self-managed shippers)
  const [manualAwbModalOpen, setManualAwbModalOpen] = useState(false);
  const [manualAwbForm, setManualAwbForm] = useState({
    order_id: '',
    awb_number: '',
    courier_name: '',
    tracking_url: '',
    shipping_cost: '',
  });
  const [manualAwbSaving, setManualAwbSaving] = useState(false);

  // Pickup-scheduling modal state (Manifest tab → "Schedule Pickup" CTA)
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const [pickupShipment, setPickupShipment] = useState(null);

  // iter-229d-fix: Pickup warehouse Delhivery registration status — used to
  // render a prominent banner at the top of every tab so vendors notice the
  // gap before they hit a 400 from AWB creation.
  const [warehouseStatus, setWarehouseStatus] = useState(null);
  const [warehouseRetrying, setWarehouseRetrying] = useState(false);

  const fetchWarehouseStatus = async () => {
    try {
      const data = await apiCall('/api/shipping/vendor/pickup-warehouse-status');
      setWarehouseStatus(data);
    } catch (_err) {
      // Silent failure — banner just won't render. Don't block the page.
      setWarehouseStatus(null);
    }
  };

  const retryWarehouseRegistration = async () => {
    if (!warehouseStatus?.location_id) {
      // No location at all — push them to the Pickup Locations tab
      setActiveTab('pickup-locations');
      return;
    }
    if (warehouseStatus.status === 'incomplete') {
      // Missing fields — open the Pickup Locations tab so they can fix it
      setActiveTab('pickup-locations');
      toast({
        title: 'Complete pickup location',
        description: `Add the missing fields: ${(warehouseStatus.missing_fields || []).join(', ')}.`,
      });
      return;
    }
    try {
      setWarehouseRetrying(true);
      const res = await apiCall(
        `/api/shipping/vendor/pickup-locations/${warehouseStatus.location_id}/register-delhivery`,
        { method: 'POST' }
      );
      toast({
        title: res?.success ? 'Registered with Delhivery' : 'Registration retried',
        description: res?.message || 'Pickup warehouse is now registered.',
      });
      await fetchWarehouseStatus();
    } catch (err) {
      const detail = err?.detail || err?.message;
      toast({
        title: 'Registration failed',
        description: typeof detail === 'string' ? detail : (detail?.message || 'Could not register with Delhivery.'),
        variant: 'destructive',
      });
    } finally {
      setWarehouseRetrying(false);
    }
  };

  useEffect(() => {
    fetchData();
    // iter-229d-fix: keep warehouse status fresh on every tab switch so the
    // banner updates after the user fixes their pickup location or clicks
    // Register Delhivery from another surface.
    fetchWarehouseStatus();
  }, [activeTab]);

  // Refresh pipeline counts independently of the active tab data
  useEffect(() => {
    (async () => {
      try {
        const data = await apiCall('/api/shipping/vendor/pipeline-summary');
        setPipeline(data || { counts: {}, totals: {} });
      } catch {}
    })();
  }, [activeTab]);

  const submitManualAwb = async () => {
    const f = manualAwbForm;
    if (!f.order_id || !f.awb_number || !f.courier_name) {
      toast({
        title: 'Missing fields',
        description: 'Order, AWB number, and Courier name are required.',
        variant: 'destructive',
      });
      return;
    }
    try {
      setManualAwbSaving(true);
      const createRes = await apiCall('/api/shipping/vendor/manual-awb', {
        method: 'POST',
        body: JSON.stringify({
          order_id: f.order_id,
          awb_number: f.awb_number.trim(),
          courier_name: f.courier_name.trim(),
          tracking_url: f.tracking_url || null,
          shipping_cost: f.shipping_cost ? parseFloat(f.shipping_cost) : null,
        }),
      });
      // iter-229d — chain into the self-managed flow: generate OTP +
      // send WABA template + advance canonical_state to PICKED_UP.
      let otpHint = '';
      if (createRes?.shipment_id) {
        try {
          const startRes = await apiCall(
            `/api/shipments/${createRes.shipment_id}/start-self-managed`,
            {
              method: 'POST',
              body: JSON.stringify({
                courier_name: f.courier_name.trim(),
                awb_number: f.awb_number.trim(),
                tracking_url: f.tracking_url || null,
              }),
            }
          );
          if (startRes?.waba_send?.success) {
            otpHint = ' OTP sent to customer via WhatsApp.';
          } else if (startRes?.waba_send?.error) {
            otpHint = ` (WhatsApp send: ${startRes.waba_send.error}). OTP still visible to customer in their order page.`;
          }
        } catch (otpErr) {
          // Non-fatal — AWB was registered, OTP step failed. Let vendor know.
          otpHint = ' AWB saved, but OTP send failed — you can retry from the In-Transit tab.';
        }
      }
      toast({
        title: 'Self-managed shipment started',
        description: `${f.courier_name} ${f.awb_number} attached.${otpHint}`,
      });
      setManualAwbModalOpen(false);
      setManualAwbForm({ order_id: '', awb_number: '', courier_name: '', tracking_url: '', shipping_cost: '' });
      fetchData();
    } catch (err) {
      toast({
        title: 'Could not register AWB',
        description: err?.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setManualAwbSaving(false);
    }
  };
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'pending') {
        // Backend `/orders-ready-to-ship` doesn't filter out orders that already
        // have an active shipment — the new pipeline-summary does. Pull both
        // and use pipeline.pending as the source of truth (it excludes orders
        // with non-cancelled shipments). Falls back to the legacy endpoint if
        // pipeline-summary fails for any reason.
        try {
          const pipe = await apiCall('/api/shipping/vendor/pipeline-summary');
          setPipeline(pipe || { counts: {}, totals: {} });
          if (Array.isArray(pipe?.pending)) {
            setPendingOrders(pipe.pending);
          } else {
            const data = await apiCall('/api/shipping/vendor/orders-ready-to-ship');
            setPendingOrders((data.orders || []).filter(o => !o.awb_number && !o.shipment_id));
          }
        } catch {
          const data = await apiCall('/api/shipping/vendor/orders-ready-to-ship');
          setPendingOrders((data.orders || []).filter(o => !o.awb_number && !o.shipment_id));
        }
      } else if (activeTab === 'shipped') {
        const data = await apiCall('/api/shipping/vendor/shipments');
        setShipments(data.shipments || []);
      } else if (activeTab === 'manifest') {
        // Manifest tab pulls from pipeline.manifest (shipments with AWB, awaiting
        // pickup scheduling). The legacy `/manifest?date=today` returned the
        // daily Delhivery-only manifest summary which mismatched the new tab
        // semantics ("orders ready to schedule pickup").
        try {
          const pipe = await apiCall('/api/shipping/vendor/pipeline-summary');
          setPipeline(pipe || { counts: {}, totals: {} });
        } catch {}
      } else if (activeTab === 'my-deliveries') {
        const data = await apiCall('/api/orders/vendor/self-delivery');
        setSelfDeliveryOrders(data.orders || []);
      } else if (activeTab === 'pickup-locations') {
        const data = await apiCall('/api/shipping/vendor/pickup-locations');
        setPickupLocations(data.locations || []);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const openPackShipModal = async (order) => {
    setSelectedOrder(order);
    
    // Try to pre-fill weight/dimensions from listing data
    let prefillWeight = '';
    let prefillLength = '';
    let prefillBreadth = '';
    let prefillHeight = '';
    
    try {
      // Get the first item's listing details for weight/dimension pre-fill
      const firstItem = order.items?.[0];
      const listingId = firstItem?.listing_id || firstItem?.product_id;
      if (listingId) {
        const listingData = await apiCall(`/api/listings/${listingId}`);
        const data = listingData?.data || listingData || {};
        if (data.weight) {
          const weightUnit = (data.weightUnit || 'kg').toLowerCase();
          prefillWeight = weightUnit === 'g' ? (parseFloat(data.weight) / 1000).toFixed(3) : String(data.weight);
        }
        if (data.length) prefillLength = String(data.length);
        if (data.breadth || data.width) prefillBreadth = String(data.breadth || data.width);
        if (data.height) prefillHeight = String(data.height);
      }
    } catch (err) {
      // Silently fail — vendor can still enter manually
      console.log('Could not pre-fill from listing:', err);
    }
    
    setPackingDetails({
      weight_kg: prefillWeight,
      length_cm: prefillLength,
      breadth_cm: prefillBreadth,
      height_cm: prefillHeight,
      insurance_opt_in: order.total_amount > 5000
    });
    setRatePreview(null);
    setAwbResult(null);
    setPackShipModalOpen(true);
  };
  
  const previewRate = async () => {
    if (!packingDetails.weight_kg || !packingDetails.length_cm || 
        !packingDetails.breadth_cm || !packingDetails.height_cm) {
      toast({
        title: 'Missing Information',
        description: 'Please fill all dimension fields',
        variant: 'destructive'
      });
      return;
    }
    
    if (parseFloat(packingDetails.weight_kg) < 0.05) {
      toast({
        title: 'Invalid Weight',
        description: 'Weight must be at least 0.05 kg (50 grams)',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setPreviewLoading(true);
      const data = await apiCall('/api/shipping/vendor/preview-rate', {
        method: 'POST',
        body: JSON.stringify({
          order_id: selectedOrder.id,
          weight_kg: parseFloat(packingDetails.weight_kg),
          length_cm: parseFloat(packingDetails.length_cm),
          breadth_cm: parseFloat(packingDetails.breadth_cm),
          height_cm: parseFloat(packingDetails.height_cm)
        })
      });
      setRatePreview(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to get rate preview',
        variant: 'destructive'
      });
    } finally {
      setPreviewLoading(false);
    }
  };
  
  const generateAWB = async () => {
    if (!ratePreview) {
      toast({
        title: 'Preview Required',
        description: 'Please preview the rate first',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setGeneratingLabel(true);
      const data = await apiCall('/api/shipping/vendor/generate-label', {
        method: 'POST',
        body: JSON.stringify({
          order_id: selectedOrder.id,
          weight_kg: parseFloat(packingDetails.weight_kg),
          length_cm: parseFloat(packingDetails.length_cm),
          breadth_cm: parseFloat(packingDetails.breadth_cm),
          height_cm: parseFloat(packingDetails.height_cm),
          insurance_opt_in: packingDetails.insurance_opt_in
        })
      });
      
      setAwbResult(data.shipment || data);
      
      toast({
        title: 'AWB Generated!',
        description: `AWB: ${data.shipment?.awb || 'N/A'} — You can now download the shipping label.`
      });
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate AWB',
        variant: 'destructive'
      });
    } finally {
      setGeneratingLabel(false);
    }
  };

  const downloadLabel = async () => {
    if (!awbResult?.label_url) {
      toast({
        title: 'Label Not Available',
        description: 'No label URL available. Try from the shipments list.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLabelDownloading(true);
      const res = await downloadAuthedFile(
        awbResult.label_url,
        `${selectedOrder?.id || awbResult.awb || 'order'}_label.pdf`,
      );
      if (res.ok) {
        toast({ title: 'Label Downloaded', description: 'Print and attach to your package.' });
        setPackShipModalOpen(false);
        fetchData();
      } else {
        toast({
          title: 'Could not download label',
          description: res.status === 401
            ? 'Session expired — please refresh and log in again.'
            : (res.error || 'Delhivery label endpoint did not respond. Try again in a moment.'),
          variant: 'destructive',
        });
      }
    } finally {
      setLabelDownloading(false);
    }
  };

  const saveDimensionsToListing = async () => {
    if (!ratePreview?.listing_id) return;
    try {
      setSavingDimensions(true);
      await apiCall('/api/shipping/vendor/save-listing-dimensions', {
        method: 'POST',
        body: JSON.stringify({
          listing_id: ratePreview.listing_id,
          weight_kg: parseFloat(packingDetails.weight_kg),
          length_cm: parseFloat(packingDetails.length_cm),
          breadth_cm: parseFloat(packingDetails.breadth_cm),
          height_cm: parseFloat(packingDetails.height_cm)
        })
      });
      toast({ title: 'Dimensions Saved', description: 'These dimensions will be used for future shipping quotes on this product.' });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to save dimensions', variant: 'destructive' });
    } finally {
      setSavingDimensions(false);
    }
  };

  // === Live Tracking ===
  const fetchLiveTracking = async (orderId) => {
    setTrackingLoading(true);
    setTrackingDialogOpen(true);
    try {
      const data = await apiCall(`/api/shipping/order/${orderId}/live-tracking`);
      setTrackingData(data);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to fetch tracking', variant: 'destructive' });
      setTrackingData(null);
    } finally {
      setTrackingLoading(false);
    }
  };

  // === Cancel AWB at Delhivery ===
  const handleCancelAwb = async (shipment) => {
    if (!shipment?.awb || !shipment?.order_id) return;
    const ok = window.confirm(
      `Cancel AWB ${shipment.awb} with Delhivery?\n\nThe shipment will be marked cancelled at Delhivery and in Aniyor. The order itself stays active — you can regenerate an AWB afterwards.`
    );
    if (!ok) return;
    try {
      setCancellingAwbId(shipment.id);
      const res = await apiCall(`/api/delhivery/shipments/${shipment.order_id}/cancel`, { method: 'POST' });
      toast({
        title: 'AWB cancelled',
        description: `AWB ${res?.awb || shipment.awb} cancelled at Delhivery.`,
      });
      // Refresh shipments list so the cancelled row moves to the correct state.
      await fetchData();
    } catch (error) {
      toast({
        title: 'Cancel AWB failed',
        description: error?.message || 'Could not cancel AWB. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCancellingAwbId(null);
    }
  };
  
  // === Confirm Order (auto-create shipment) ===
  const confirmOrder = async (orderId) => {
    setConfirmingOrder(prev => ({ ...prev, [orderId]: true }));
    try {
      const result = await apiCall(`/api/shipping/vendor/orders/${orderId}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ auto_create_shipment: true })
      });
      
      toast({
        title: result.shipment?.awb ? 'Order Confirmed & Shipment Created' : 'Order Confirmed',
        description: result.shipment?.awb
          ? `AWB: ${result.shipment.awb} (${result.shipment.provider})`
          : 'Shipment will be created when you generate a label.'
      });
      
      setConfirmedOrders(prev => new Set([...prev, orderId]));
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to confirm order', variant: 'destructive' });
    } finally {
      setConfirmingOrder(prev => ({ ...prev, [orderId]: false }));
    }
  };
  
  // === Pickup Location CRUD ===
  const savePickupLocation = async () => {
    if (!pickupForm.name || !pickupForm.address_line || !pickupForm.pincode || !pickupForm.phone) {
      toast({ title: 'Missing fields', description: 'Please fill in name, address, pincode, and phone.', variant: 'destructive' });
      return;
    }
    setPickupSaving(true);
    try {
      const method = editingPickup ? 'PUT' : 'POST';
      const url = editingPickup
        ? `/api/shipping/vendor/pickup-locations/${editingPickup}`
        : '/api/shipping/vendor/pickup-locations';
      
      await apiCall(url, { method, body: JSON.stringify(pickupForm) });
      toast({ title: editingPickup ? 'Location Updated' : 'Location Created' });
      setPickupForm({ name: '', address_line: '', city: '', state: '', pincode: '', phone: '', is_default: false });
      setEditingPickup(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to save location', variant: 'destructive' });
    } finally {
      setPickupSaving(false);
    }
  };
  
  const deletePickupLocation = async (locationId) => {
    try {
      await apiCall(`/api/shipping/vendor/pickup-locations/${locationId}`, { method: 'DELETE' });
      toast({ title: 'Location deleted' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to delete', variant: 'destructive' });
    }
  };
  
  const registerWithDelhivery = async (locationId) => {
    try {
      const result = await apiCall(`/api/shipping/vendor/pickup-locations/${locationId}/register-delhivery`, { method: 'POST' });
      toast({
        title: result.success ? 'Registered with Delhivery' : 'Registration Failed',
        description: result.message,
        variant: result.success ? 'default' : 'destructive'
      });
      fetchData();
      fetchWarehouseStatus();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to register with Delhivery', variant: 'destructive' });
    }
  };
  
  const updateDeliveryStatus = async (orderId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [orderId]: true }));
    try {
      const payload = { status: newStatus };
      if (newStatus === 'dispatched') {
        payload.tracking_id = selfTrackingInput[orderId] || '';
        payload.tracking_provider = selfTrackingProvider[orderId] || 'Self';
      }
      await apiCall(`/api/orders/${orderId}/vendor-delivery-status`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const statusLabels = { preparing: 'Preparing', dispatched: 'Dispatched', delivered: 'Delivered' };
      toast({ title: `Order marked as ${statusLabels[newStatus]}` });
      const data = await apiCall('/api/orders/vendor/self-delivery');
      setSelfDeliveryOrders(data.orders || []);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update status', variant: 'destructive' });
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const getNextDeliveryStatus = (current) => {
    const flow = { pending: 'preparing', processing: 'preparing', processing_fulfillment: 'preparing', preparing: 'dispatched', dispatched: 'delivered' };
    return flow[current] || null;
  };

  const getNextDeliveryLabel = (current) => {
    const labels = { pending: 'Mark Preparing', processing: 'Mark Preparing', processing_fulfillment: 'Mark Preparing', preparing: 'Mark Dispatched', dispatched: 'Mark Delivered' };
    return labels[current] || null;
  };

  const getDeliveryStatusBadge = (status) => {
    const config = {
      pending:     { cls: 'bg-gray-100 text-gray-700',   label: 'Pending' },
      processing:  { cls: 'bg-yellow-100 text-yellow-800', label: 'Processing' },
      processing_fulfillment: { cls: 'bg-yellow-100 text-yellow-800', label: 'Processing' },
      preparing:   { cls: 'bg-blue-100 text-blue-700',   label: 'Preparing' },
      dispatched:  { cls: 'bg-purple-100 text-purple-700', label: 'Dispatched' },
      delivered:   { cls: 'bg-green-100 text-green-700', label: 'Delivered' },
    };
    const c = config[status] || { cls: 'bg-gray-100 text-gray-700', label: status };
    return <Badge className={c.cls}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'LABEL_GENERATED': { color: 'bg-blue-100 text-blue-700', label: 'Label Generated' },
      'PICKED_UP': { color: 'bg-purple-100 text-purple-700', label: 'Picked Up' },
      'IN_TRANSIT': { color: 'bg-orange-100 text-orange-700', label: 'In Transit' },
      'OUT_FOR_DELIVERY': { color: 'bg-yellow-100 text-yellow-700', label: 'Out for Delivery' },
      'DELIVERED': { color: 'bg-green-100 text-green-700', label: 'Delivered' },
      'RTO': { color: 'bg-red-100 text-red-700', label: 'RTO' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };
  
  return (
    <DashboardLayout>
      <div className="p-2 sm:p-4 md:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              <Package className="h-5 w-5 md:h-6 md:w-6" />
              Pack & Ship
            </h1>
            <p className="text-gray-500 text-xs md:text-sm mt-1">
              Process orders and generate shipping labels
            </p>
          </div>
          <div className="flex gap-2">
            <InvoiceHeaderDownload apiCall={apiCall} />
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
        
        {/* iter-229d-fix: Pickup Warehouse Registration Banner.
             Renders only when the vendor's primary pickup is NOT registered
             with Delhivery — gives a single-click CTA to fix it before they
             try to generate an AWB. The same panel also lists which fields
             are missing so the vendor knows exactly what to update. */}
        {warehouseStatus && !warehouseStatus.ok && (
          <div
            data-testid="warehouse-registration-banner"
            className={`mb-4 rounded-lg border p-3 md:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
              warehouseStatus.status === 'missing_location'
                ? 'border-red-300 bg-red-50'
                : 'border-amber-300 bg-amber-50'
            }`}
          >
            <div className="flex items-start gap-3">
              <AlertCircle
                className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                  warehouseStatus.status === 'missing_location' ? 'text-red-600' : 'text-amber-600'
                }`}
              />
              <div className="text-sm">
                <p className="font-semibold text-gray-900">
                  {warehouseStatus.status === 'missing_location'
                    ? 'No pickup location configured'
                    : warehouseStatus.status === 'incomplete'
                      ? 'Pickup location is incomplete'
                      : warehouseStatus.status === 'registration_failed'
                        ? 'Delhivery rejected your pickup warehouse'
                        : 'Pickup warehouse registration pending'}
                </p>
                <p className="text-gray-700 mt-0.5">
                  {warehouseStatus.message}
                </p>
                {(warehouseStatus.missing_fields || []).length > 0 && (
                  <p className="text-xs text-amber-800 mt-1">
                    Missing: {(warehouseStatus.missing_fields || []).join(', ')}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  AWB labels can't be printed until your pickup warehouse is registered with Delhivery.
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                data-testid="warehouse-banner-cta"
                onClick={retryWarehouseRegistration}
                disabled={warehouseRetrying}
              >
                {warehouseRetrying ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4 mr-1" />
                )}
                {warehouseStatus.status === 'missing_location'
                  ? 'Add Pickup Location'
                  : warehouseStatus.status === 'incomplete'
                    ? 'Complete Location'
                    : 'Register Pickup Warehouse'}
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex w-auto min-w-max gap-0">
              <TabsTrigger value="manifest" data-testid="tab-manifest" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <Calendar className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Manifest</span>
                <span className="ml-1 px-1.5 rounded bg-indigo-100 text-indigo-800 text-[10px]">{pipeline.counts?.manifest ?? 0}</span>
              </TabsTrigger>
              <TabsTrigger value="pickup" data-testid="tab-pickup" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <Calendar className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Pickup</span>
                <span className="ml-1 px-1.5 rounded bg-purple-100 text-purple-800 text-[10px]">{pipeline.counts?.pickup ?? 0}</span>
              </TabsTrigger>
              <TabsTrigger value="shipped" data-testid="tab-shipped" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <Truck className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Shipped</span>
                <span className="ml-1 px-1.5 rounded bg-cyan-100 text-cyan-800 text-[10px]">{pipeline.counts?.shipped ?? 0}</span>
              </TabsTrigger>
              <TabsTrigger value="in_transit" data-testid="tab-in-transit" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <Navigation className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">In-Transit</span>
                <span className="ml-1 px-1.5 rounded bg-blue-100 text-blue-800 text-[10px]">{pipeline.counts?.in_transit ?? 0}</span>
              </TabsTrigger>
              <TabsTrigger value="delivered" data-testid="tab-delivered" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <CheckCircle className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Delivered</span>
                <span className="ml-1 px-1.5 rounded bg-emerald-100 text-emerald-800 text-[10px]">{pipeline.counts?.delivered ?? 0}</span>
              </TabsTrigger>
              <TabsTrigger value="ndr" data-testid="tab-ndr" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <Clock className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">NDR</span>
                <span className="ml-1 px-1.5 rounded bg-amber-100 text-amber-800 text-[10px]">{pipeline.counts?.ndr ?? 0}</span>
              </TabsTrigger>
              <TabsTrigger value="rto" data-testid="tab-rto" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <Truck className="h-3.5 w-3.5 md:h-4 md:w-4 rotate-180" />
                <span className="hidden sm:inline">RTO</span>
                <span className="ml-1 px-1.5 rounded bg-rose-100 text-rose-800 text-[10px]">{pipeline.counts?.rto ?? 0}</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Self-managed shippers: register AWB obtained from a non-Delhivery courier */}
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              data-testid="manual-awb-cta"
              onClick={() => setManualAwbModalOpen(true)}
            >
              <FileText className="h-4 w-4 mr-1" />
              Add AWB manually (self-managed)
            </Button>
          </div>
          
          {/* Pending Orders Tab */}
          <TabsContent value="pending">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : pendingOrders.length === 0 ? (
              <Card className="p-4 md:p-8 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-700">All caught up!</h3>
                <p className="text-gray-500">No pending orders to ship</p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pendingOrders.map((order) => (
                  <Card key={order.id}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="font-semibold text-sm">#{order.order_number}</span>
                            <Badge variant="outline" className="text-xs">{order.payment_method}</Badge>
                            <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                              {order.fulfillment_status || 'Pending'}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{order.destination_pincode}</span>
                            </div>
                            <div className="truncate">{order.customer_name}</div>
                            <div className="font-medium">{formatPrice(order.total_amount || 0)}</div>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => confirmOrder(order.id)}
                            disabled={confirmingOrder[order.id] || confirmedOrders.has(order.id)}
                            data-testid={`confirm-order-${order.id}`}
                          >
                            {confirmingOrder[order.id] ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : confirmedOrders.has(order.id) ? (
                              <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-1" />
                            )}
                            {confirmedOrders.has(order.id) ? 'Confirmed' : 'Confirm'}
                          </Button>
                          {/* If the order already has an AWB (e.g. label was generated
                              earlier and the row hasn't yet moved out of pending),
                              expose a direct Print Label CTA. Same auth-proxy
                              endpoint as the Manifest/Shipped tabs. */}
                          {(order.awb_number || order.shipment_id) && (
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`pending-print-label-${order.id}`}
                              onClick={async () => {
                                const url = order.label_url || (order.shipping_provider?.toLowerCase() === 'delhivery' || !order.shipping_provider
                                  ? `/api/delhivery/shipments/${order.id}/label`
                                  : null);
                                if (!url) {
                                  toast({
                                    title: 'Label unavailable',
                                    description: 'This courier does not provide a downloadable label here. Use the Manifest tab to view tracking info.',
                                    variant: 'destructive',
                                  });
                                  return;
                                }
                                const res = await downloadAuthedFile(url, `${order.order_number || order.id}_label.pdf`);
                                if (!res.ok) {
                                  toast({
                                    title: 'Could not download label',
                                    description: res.status === 401 ? 'Session expired — please refresh.' : (res.error || 'Try again from Manifest tab.'),
                                    variant: 'destructive',
                                  });
                                }
                              }}
                            >
                              <Printer className="h-4 w-4 mr-1" />
                              Print Label
                            </Button>
                          )}
                          <Button
                            size="sm"
                            disabled={!!(order.awb_number || order.shipment_id)}
                            title={(order.awb_number || order.shipment_id) ? `AWB ${order.awb_number || ''} already generated — see Manifest tab` : 'Generate AWB and shipping label'}
                            onClick={() => {
                              if (order.awb_number || order.shipment_id) {
                                toast({
                                  title: 'AWB already generated',
                                  description: `This order already has shipment ${order.awb_number || order.shipment_id}. Find it in the Manifest tab.`,
                                });
                                setActiveTab('manifest');
                                return;
                              }
                              openPackShipModal(order);
                            }}
                            data-testid={`ship-btn-${order.id}`}
                          >
                            <Box className="h-4 w-4 mr-1" />
                            {(order.awb_number || order.shipment_id) ? 'Shipped' : 'Ship'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Shipped Tab */}
          <TabsContent value="shipped">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : shipments.length === 0 ? (
              <Card className="p-4 md:p-8 text-center">
                <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700">No shipments yet</h3>
                <p className="text-gray-500">Your shipped orders will appear here</p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {shipments.map((shipment) => {
                  // iter-235 SSOT — read canonical CTAs from the backend's
                  // shipment_state (or fall back to local derivation if
                  // not yet stamped). Replaces ad-hoc isAwbCancelled / status
                  // checks per surface.
                  const _ss = readShipmentState(shipment);
                  const isAwbCancelled = _ss.state === 'AWB_CANCELLED';
                  const canPrintLabel  = _ss.ctas.includes('print_label');
                  const canTrack       = _ss.ctas.includes('track_shipment');
                  const canCancelAwb   = _ss.ctas.includes('cancel_awb');
                  const canRegenAwb    = _ss.ctas.includes('ship_with_delhivery');
                  const cancelledOn = shipment.awb_cancelled_at || shipment.cancelled_at;
                  return (
                  <Card key={shipment.id} data-testid={`shipment-card-${shipment.id}`}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span
                              className={`font-mono text-xs sm:text-sm ${isAwbCancelled ? 'line-through text-gray-500' : ''}`}
                              data-testid={`awb-number-${shipment.id}`}
                            >
                              {shipment.awb}
                            </span>
                            {isAwbCancelled ? (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-[10px] sm:text-xs font-semibold px-2 py-0.5"
                                data-testid={`awb-cancelled-badge-${shipment.id}`}
                              >
                                <span aria-hidden="true">🔴</span> AWB Cancelled
                              </span>
                            ) : (
                              getStatusBadge(shipment.status)
                            )}
                            <span className="text-xs sm:text-sm text-gray-500">{shipment.provider}</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 text-sm text-gray-600">
                            <div className="truncate">Order: {shipment.order_id?.slice(0, 8)}...</div>
                            <div>{shipment.dimensions?.billable_weight_kg} kg</div>
                            <div>{formatPrice(shipment.total_logistics_cost || 0)}</div>
                          </div>
                          {isAwbCancelled && cancelledOn && (
                            <div
                              className="text-xs text-gray-500 mt-1"
                              data-testid={`awb-cancelled-on-${shipment.id}`}
                            >
                              Cancelled on {new Date(cancelledOn).toLocaleString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })}
                              {shipment.delhivery_cancel_reason && (
                                <span className="text-gray-400"> · {shipment.delhivery_cancel_reason}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {canPrintLabel && shipment.label_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const res = await downloadAuthedFile(
                                  shipment.label_url,
                                  `${shipment.order_id || 'order'}_label.pdf`,
                                );
                                if (!res.ok) {
                                  toast({
                                    title: 'Could not download label',
                                    description: res.status === 401 ? 'Session expired — please refresh.' : (res.error || 'Try again.'),
                                    variant: 'destructive',
                                  });
                                }
                              }}
                              data-testid={`print-label-${shipment.id}`}
                            >
                              <Printer className="h-4 w-4 mr-1" />
                              <span className="hidden sm:inline">Print</span> Label
                            </Button>
                          )}
                          {canTrack && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => fetchLiveTracking(shipment.order_id)}
                              data-testid={`track-shipment-${shipment.id}`}
                            >
                              <Navigation className="h-4 w-4 mr-1" />
                              Track
                            </Button>
                          )}
                          {canCancelAwb && shipment.awb && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-200 text-red-700 hover:bg-red-50"
                              onClick={() => handleCancelAwb(shipment)}
                              disabled={cancellingAwbId === shipment.id}
                              data-testid={`cancel-awb-${shipment.id}`}
                            >
                              <X className="h-4 w-4 mr-1" />
                              {cancellingAwbId === shipment.id ? 'Cancelling…' : 'Cancel AWB'}
                            </Button>
                          )}
                          {canRegenAwb && isAwbCancelled && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => {
                                setActiveTab('pending');
                                toast({
                                  title: 'Regenerate AWB',
                                  description: 'Find the order in the Pending tab and click "Ship" to generate a fresh AWB.',
                                });
                              }}
                              data-testid={`generate-new-awb-${shipment.id}`}
                            >
                              <Box className="h-4 w-4 mr-1" />
                              Generate New AWB
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
          
          {/* Manifest Tab — shipments with AWB + label, awaiting pickup scheduling.
              Replaces the old "today's daily Delhivery summary" view with a
              pipeline-driven list that exposes per-shipment Schedule Pickup,
              Download Label, and a bulk-manifest-PDF CTA. */}
          <TabsContent value="manifest">
            {(pipeline.manifest || []).length === 0 ? (
              <Card className="p-4 md:p-8 text-center" data-testid="manifest-empty">
                <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700">No shipments awaiting pickup</h3>
                <p className="text-gray-500">Generate AWBs from the Pending tab — they'll show up here ready for pickup scheduling.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {/* Summary strip */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  <Card className="p-3 md:p-4">
                    <div className="text-xl md:text-2xl font-bold text-blue-600">{(pipeline.manifest || []).length}</div>
                    <div className="text-xs md:text-sm text-gray-500">Awaiting pickup</div>
                  </Card>
                  <Card className="p-3 md:p-4">
                    <div className="text-xl md:text-2xl font-bold text-green-600">
                      {(pipeline.manifest || []).reduce((acc, s) => acc + (parseFloat(s.weight_kg) || 0.5), 0).toFixed(1)} kg
                    </div>
                    <div className="text-xs md:text-sm text-gray-500">Total weight</div>
                  </Card>
                  <Card className="p-3 md:p-4">
                    <div className="text-xl md:text-2xl font-bold text-purple-600">
                      {formatPrice((pipeline.manifest || []).reduce((acc, s) => acc + (parseFloat(s.total_logistics_cost) || 0), 0))}
                    </div>
                    <div className="text-xs md:text-sm text-gray-500">Logistics cost</div>
                  </Card>
                </div>

                {/* Bulk manifest PDF — Delhivery shipments only */}
                {(pipeline.manifest || []).some(s => (s.provider || '').toLowerCase() === 'delhivery') && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="bulk-manifest-pdf-btn"
                      onClick={async () => {
                        const ids = (pipeline.manifest || [])
                          .filter(s => (s.provider || '').toLowerCase() === 'delhivery' && s.id)
                          .map(s => s.id);
                        if (ids.length === 0) return;
                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/shipping/vendor/manifest/bulk-pdf`;
                        const token = sessionStorage.getItem('token') || '';
                        try {
                          const resp = await fetch(url, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({ shipment_ids: ids }),
                          });
                          if (!resp.ok) {
                            const txt = await resp.text();
                            toast({ title: 'Manifest failed', description: txt.slice(0, 160), variant: 'destructive' });
                            return;
                          }
                          const blob = await resp.blob();
                          const blobUrl = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = blobUrl;
                          a.download = `manifest_${new Date().toISOString().slice(0,10)}.pdf`;
                          document.body.appendChild(a); a.click(); a.remove();
                          window.URL.revokeObjectURL(blobUrl);
                        } catch (e) {
                          toast({ title: 'Manifest failed', description: e?.message || 'Try again', variant: 'destructive' });
                        }
                      }}
                    >
                      <FileText className="h-4 w-4 mr-1" /> Bulk Manifest PDF (Delhivery)
                    </Button>
                  </div>
                )}

                {/* Per-shipment cards with Schedule Pickup CTA */}
                <div className="grid gap-3" data-testid="manifest-list">
                  {(pipeline.manifest || []).map((sh) => {
                    const meta = sh._order || {};
                    const origin = sh._origin || {};
                    const labelHref = sh.label_url || (sh.awb && sh.order_id ? `/api/delhivery/shipments/${sh.order_id}/label` : null);
                    const destStr = [meta.destination_city, meta.destination_state, meta.destination_pincode].filter(Boolean).join(', ');
                    const originStr = [origin.city, origin.state, origin.pincode].filter(Boolean).join(', ');
                    return (
                      <Card key={sh.id} data-testid={`manifest-row-${sh.id}`}>
                        <CardContent className="p-3 md:p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {/* Top strip: order # + AWB + courier badge */}
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge variant="default" className="text-xs bg-gray-900 text-white">
                                  {meta.order_number || `#${(sh.order_id || '').slice(0, 8)}`}
                                </Badge>
                                <span className="font-mono text-xs sm:text-sm font-semibold text-blue-700" data-testid={`manifest-awb-${sh.id}`}>
                                  AWB {sh.awb}
                                </span>
                                <Badge variant="outline" className="text-[10px]">
                                  {(sh.courier_name || sh.provider || 'carrier').toString().toUpperCase()}
                                </Badge>
                                {sh.provider === 'self_managed' && (
                                  <Badge variant="outline" className="text-[10px] bg-purple-50 border-purple-200 text-purple-700">
                                    Self-managed
                                  </Badge>
                                )}
                              </div>
                              {/* From → To context block (replaces the old single-line strip).
                                  Adds visible sender origin so vendors can sanity-check
                                  the right warehouse/pickup point is on the label. */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-700 mt-1">
                                <div className="space-y-0.5" data-testid={`manifest-from-${sh.id}`}>
                                  <div className="text-[10px] uppercase text-gray-500 tracking-wide font-semibold">From</div>
                                  <div className="font-medium truncate">{origin.name || '—'}</div>
                                  <div className="text-gray-500 text-[11px] truncate">{originStr || 'Pickup address not available'}</div>
                                </div>
                                <div className="space-y-0.5" data-testid={`manifest-to-${sh.id}`}>
                                  <div className="text-[10px] uppercase text-gray-500 tracking-wide font-semibold">To</div>
                                  <div className="font-medium truncate">{meta.customer_name || '—'}</div>
                                  <div className="text-gray-500 text-[11px] truncate">{destStr || 'Destination address not available'}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1.5">
                                {sh.weight_kg && <span>{sh.weight_kg}kg</span>}
                                {sh.total_logistics_cost ? <span>· Cost {formatPrice(sh.total_logistics_cost)}</span> : null}
                                {meta.items_count ? <span>· {meta.items_count} item(s)</span> : null}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 flex-shrink-0">
                              {labelHref && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={async () => {
                                    const orderName = (meta.order_number || sh.order_id || sh.id).toString().replace('#','').replace(/\//g, '_');
                                    const res = await downloadAuthedFile(labelHref, `${orderName}_label.pdf`);
                                    if (!res.ok) {
                                      toast({
                                        title: 'Could not download label',
                                        description: res.status === 401
                                          ? 'Session expired — please refresh.'
                                          : (res.error || 'Try again — Delhivery may need a minute to finalise the label.'),
                                        variant: 'destructive',
                                      });
                                    }
                                  }}
                                  data-testid={`manifest-label-${sh.id}`}
                                >
                                  <Printer className="h-4 w-4 mr-1" /> Print Label
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`manifest-schedule-pickup-${sh.id}`}
                                disabled={(() => {
                                  // iter-229a — UI lock: once the canonical
                                  // state machine is past pickup_scheduled
                                  // (or the legacy pipeline status reports
                                  // pickup_done / in_transit / delivered),
                                  // disable the button so duplicate pickup
                                  // requests can't be sent to Delhivery.
                                  const LOCKED = new Set([
                                    'pickup_scheduled', 'picked_up', 'in_transit',
                                    'delivered', 'return_window_elapsed',
                                  ]);
                                  if (LOCKED.has(sh.canonical_state)) return true;
                                  // Legacy fallback for shipments created
                                  // before the canonical_state field was added.
                                  const legacy = (sh.status || '').toUpperCase();
                                  return [
                                    'PICKUP_SCHEDULED', 'PICKED_UP', 'IN_TRANSIT',
                                    'OUT_FOR_DELIVERY', 'DELIVERED',
                                    'RTO', 'RTO_IN_TRANSIT', 'RTO_DELIVERED',
                                  ].includes(legacy);
                                })()}
                                title={(() => {
                                  const LOCKED = new Set([
                                    'pickup_scheduled', 'picked_up', 'in_transit',
                                    'delivered', 'return_window_elapsed',
                                  ]);
                                  if (LOCKED.has(sh.canonical_state)) {
                                    return 'Pickup already scheduled with Delhivery — cannot rebook.';
                                  }
                                  return '';
                                })()}
                                onClick={() => {
                                  setPickupShipment(sh);
                                  setPickupModalOpen(true);
                                }}
                              >
                                <Calendar className="h-4 w-4 mr-1" /> Schedule Pickup
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>
          {/* My Deliveries Tab */}
          <TabsContent value="my-deliveries" data-testid="my-deliveries-content">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : selfDeliveryOrders.length === 0 ? (
              <Card className="p-4 md:p-8 text-center">
                <Navigation className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700">No active self-delivery orders</h3>
                <p className="text-gray-500 text-sm mt-1">
                  Orders you manage manually will appear here. Enable "I manage my own deliveries" on your listings, or set your delivery mode to Self Managed or Hybrid in Store Profile.
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {/* Status legend */}
                <div className="flex items-center gap-2 sm:gap-4 text-xs text-gray-500 px-1 flex-wrap">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block" /> Pending</span>
                  <ChevronRight className="h-3 w-3 hidden sm:block" />
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Preparing</span>
                  <ChevronRight className="h-3 w-3 hidden sm:block" />
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500 inline-block" /> Dispatched</span>
                  <ChevronRight className="h-3 w-3 hidden sm:block" />
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Delivered</span>
                </div>

                {selfDeliveryOrders.map((order) => {
                  const nextStatus = getNextDeliveryStatus(order.status);
                  const nextLabel = getNextDeliveryLabel(order.status);
                  const isUpdating = updatingStatus[order.id];
                  return (
                    <Card key={order.id} data-testid={`self-delivery-order-${order.id}`}>
                      <CardContent className="p-3 md:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          {/* Order info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="font-semibold text-sm">#{order.order_number}</span>
                              {getDeliveryStatusBadge(order.status)}
                              {order.order_type && (
                                <Badge variant="outline" className="text-xs">{order.order_type}</Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-600">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <User className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                <span className="truncate">{order.customer_name}</span>
                                {order.customer_phone && (
                                  <span className="text-gray-400 text-xs">· {order.customer_phone}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 min-w-0 sm:col-span-1">
                                <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                <span className="truncate text-xs">{order.delivery_address}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <ClipboardList className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                <span className="truncate text-xs">{order.items_summary}</span>
                              </div>
                            </div>
                            {order.delivery_notes && (
                              <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                                Note: {order.delivery_notes}
                              </p>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto sm:shrink-0">
                            <span className="text-sm font-semibold">
                              {formatPrice(Number(order.total_amount || 0).toLocaleString('en-IN'))}
                            </span>

                            {/* Tracking input for dispatching */}
                            {order.status === 'preparing' && (
                              <div className="flex flex-col gap-1 w-full sm:max-w-[220px]">
                                <Input
                                  placeholder="AWB / Tracking ID"
                                  className="text-xs h-7"
                                  value={selfTrackingInput[order.id] || ''}
                                  onChange={(e) => setSelfTrackingInput(p => ({ ...p, [order.id]: e.target.value }))}
                                  data-testid={`awb-input-${order.id}`}
                                />
                                <Input
                                  placeholder="Provider (e.g. BlueDart)"
                                  className="text-xs h-7"
                                  value={selfTrackingProvider[order.id] || ''}
                                  onChange={(e) => setSelfTrackingProvider(p => ({ ...p, [order.id]: e.target.value }))}
                                />
                              </div>
                            )}

                            {/* Show tracking ID if set */}
                            {order.tracking_id && (
                              <div className="text-xs text-gray-500">
                                AWB: <span className="font-mono font-medium">{order.tracking_id}</span>
                                {order.tracking_provider && <span> ({order.tracking_provider})</span>}
                              </div>
                            )}

                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/orders/${order.id}`)}
                                data-testid={`view-order-btn-${order.id}`}
                              >
                                View
                              </Button>
                              {nextStatus && (
                                <Button
                                  size="sm"
                                  disabled={isUpdating}
                                  onClick={() => updateDeliveryStatus(order.id, nextStatus)}
                                  data-testid={`advance-status-btn-${order.id}`}
                                  className={
                                    nextStatus === 'delivered'
                                      ? 'bg-green-600 hover:bg-green-700'
                                      : nextStatus === 'dispatched'
                                      ? 'bg-purple-600 hover:bg-purple-700'
                                      : 'bg-blue-600 hover:bg-blue-700'
                                  }
                                >
                                  {isUpdating ? (
                                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                  ) : (
                                    <Truck className="h-3.5 w-3.5 mr-1" />
                                  )}
                                  {nextLabel}
                                </Button>
                              )}
                              {!nextStatus && (
                                <Badge className="bg-green-100 text-green-700 px-3 py-1">
                                  <CheckCircle className="h-3.5 w-3.5 mr-1 inline" />
                                  Delivered
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
          
          {/* Pickup Locations Tab */}
          <TabsContent value="pickup-locations" data-testid="pickup-locations-content">
            <div className="space-y-4">
              {/* Add/Edit Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{editingPickup ? 'Edit Pickup Location' : 'Add Pickup Location'}</CardTitle>
                  <CardDescription>Locations where courier picks up your shipments</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Location Name</Label>
                      <Input
                        placeholder="e.g., Main Warehouse"
                        value={pickupForm.name}
                        onChange={(e) => setPickupForm(p => ({ ...p, name: e.target.value }))}
                        data-testid="pickup-name-input"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        placeholder="10-digit phone"
                        value={pickupForm.phone}
                        onChange={(e) => setPickupForm(p => ({ ...p, phone: e.target.value }))}
                        data-testid="pickup-phone-input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address</Label>
                      <Input
                        placeholder="Full address"
                        value={pickupForm.address_line}
                        onChange={(e) => setPickupForm(p => ({ ...p, address_line: e.target.value }))}
                        data-testid="pickup-address-input"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={pickupForm.city}
                        onChange={(e) => setPickupForm(p => ({ ...p, city: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={pickupForm.state}
                        onChange={(e) => setPickupForm(p => ({ ...p, state: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Pincode</Label>
                      <Input
                        placeholder="6-digit pincode"
                        maxLength={6}
                        value={pickupForm.pincode}
                        onChange={(e) => setPickupForm(p => ({ ...p, pincode: e.target.value }))}
                        data-testid="pickup-pincode-input"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch
                        checked={pickupForm.is_default}
                        onCheckedChange={(val) => setPickupForm(p => ({ ...p, is_default: val }))}
                      />
                      <Label>Set as default</Label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={savePickupLocation} disabled={pickupSaving} data-testid="save-pickup-btn">
                      {pickupSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                      {editingPickup ? 'Update Location' : 'Add Location'}
                    </Button>
                    {editingPickup && (
                      <Button variant="outline" onClick={() => {
                        setEditingPickup(null);
                        setPickupForm({ name: '', address_line: '', city: '', state: '', pincode: '', phone: '', is_default: false });
                      }}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Existing Locations */}
              {pickupLocations.length === 0 ? (
                <Card className="p-4 md:p-8 text-center">
                  <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-700">No pickup locations</h3>
                  <p className="text-gray-500">Add your warehouse or shop address above</p>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {pickupLocations.map((loc) => (
                    <Card key={loc.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-medium">{loc.name}</span>
                              {loc.is_default && <Badge className="bg-blue-100 text-blue-700">Default</Badge>}
                              {loc.delhivery_registered ? (
                                <Badge className="bg-green-100 text-green-700" title={loc.delhivery_warehouse_name ? `Registered as: ${loc.delhivery_warehouse_name}` : ''}>
                                  ✓ Delhivery Registered
                                </Badge>
                              ) : loc.delhivery_registration_error ? (
                                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700" title={loc.delhivery_registration_error}>
                                  ⚠ Registration Pending
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-gray-300 text-gray-500">
                                  Not Registered
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{loc.address_line}</p>
                            <p className="text-sm text-gray-500">{loc.city}, {loc.state} - {loc.pincode}</p>
                            <p className="text-xs text-gray-400 mt-1">Phone: {loc.phone}</p>
                            {loc.delhivery_registration_error && (
                              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 max-w-md" data-testid={`delhivery-reg-error-${loc.id}`}>
                                <strong>Carrier registration:</strong> {loc.delhivery_registration_error.length > 140 ? loc.delhivery_registration_error.slice(0, 140) + '…' : loc.delhivery_registration_error}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            {!loc.delhivery_registered && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => registerWithDelhivery(loc.id)}
                                data-testid={`register-delhivery-${loc.id}`}
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Register Delhivery
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingPickup(loc.id);
                                setPickupForm({
                                  name: loc.name,
                                  address_line: loc.address_line,
                                  city: loc.city,
                                  state: loc.state,
                                  pincode: loc.pincode,
                                  phone: loc.phone,
                                  is_default: loc.is_default
                                });
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deletePickupLocation(loc.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─────────────── PROGRESSIVE PIPELINE TABS ─────────────── */}
          {/* These render directly from the /vendor/pipeline-summary aggregate.
              Each card shows AWB + customer + destination + ETA + (where
              applicable) bulk-action checkboxes and stage-specific CTAs. */}

          {/* Forward — Pack & Ship started but no AWB yet (TAT 24-48h). */}
          <TabsContent value="forward" data-testid="forward-content">
            <PipelineList
              stage="forward"
              title="Forward"
              emptyText="No forward shipments. Click 'Ship' on a Pending order to start the flow."
              items={pipeline.forward || []}
              formatPrice={formatPrice}
            />
          </TabsContent>

          {/* Pickup — pickup scheduled, awaiting carrier scan */}
          <TabsContent value="pickup" data-testid="pickup-content">
            <PipelineList
              stage="pickup"
              title="Pickup"
              emptyText="No pickups scheduled. Schedule pickup from the Manifest tab."
              items={pipeline.pickup || []}
              formatPrice={formatPrice}
              apiCall={apiCall}
              toast={toast}
              onRefresh={async () => {
                try {
                  const data = await apiCall('/api/shipping/vendor/pipeline-summary');
                  setPipeline(data || { counts: {}, totals: {} });
                } catch {}
              }}
            />
          </TabsContent>

          {/* In-Transit */}
          <TabsContent value="in_transit" data-testid="in-transit-content">
            <PipelineList
              stage="in_transit"
              title="In-Transit"
              emptyText="No shipments in transit."
              items={pipeline.in_transit || []}
              formatPrice={formatPrice}
              apiCall={apiCall}
              toast={toast}
              onRefresh={async () => {
                try {
                  const data = await apiCall('/api/shipping/vendor/pipeline-summary');
                  setPipeline(data || { counts: {}, totals: {} });
                } catch {}
              }}
            />
          </TabsContent>

          {/* Delivered */}
          <TabsContent value="delivered" data-testid="delivered-content">
            <PipelineList
              stage="delivered"
              title="Delivered"
              emptyText="No deliveries yet."
              items={pipeline.delivered || []}
              formatPrice={formatPrice}
            />
          </TabsContent>

          {/* NDR — undelivered, vendor must re-attempt or RTO */}
          <TabsContent value="ndr" data-testid="ndr-content">
            <PipelineList
              stage="ndr"
              title="Non Delivery Report (NDR)"
              emptyText="No NDR cases. 🎉"
              items={pipeline.ndr || []}
              formatPrice={formatPrice}
              showNdrActions
              apiCall={apiCall}
              onRefresh={async () => {
                try {
                  const data = await apiCall('/api/shipping/vendor/pipeline-summary');
                  setPipeline(data || { counts: {}, totals: {} });
                } catch {}
              }}
              toast={toast}
            />
          </TabsContent>

          {/* RTO — parcel returning to vendor */}
          <TabsContent value="rto" data-testid="rto-content">
            <PipelineList
              stage="rto"
              title="Return to Origin (RTO)"
              emptyText="No returns."
              items={pipeline.rto || []}
              formatPrice={formatPrice}
            />
          </TabsContent>
        </Tabs>

        {/* Manual AWB modal (self-managed shippers) */}
        <Dialog open={manualAwbModalOpen} onOpenChange={setManualAwbModalOpen}>          <DialogContent className="max-w-md" data-testid="manual-awb-modal">
            <DialogHeader>
              <DialogTitle>Add AWB manually</DialogTitle>
              <DialogDescription>
                For shipments handled by a non-Delhivery courier (Bluedart, DTDC, India Post, FedEx, etc.). The order will move into the Manifest tab once registered.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Order *</Label>
                <select
                  data-testid="manual-awb-order-select"
                  className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={manualAwbForm.order_id}
                  onChange={(e) => setManualAwbForm({ ...manualAwbForm, order_id: e.target.value })}
                >
                  <option value="">Select an order…</option>
                  {(pipeline.pending || []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.order_number || o.shopify_order_number} — {(o.customer || {}).name || 'Customer'}
                    </option>
                  ))}
                </select>
                {(pipeline.pending || []).length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">No pending orders available.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>AWB number *</Label>
                  <Input
                    data-testid="manual-awb-number-input"
                    value={manualAwbForm.awb_number}
                    onChange={(e) => setManualAwbForm({ ...manualAwbForm, awb_number: e.target.value })}
                    placeholder="e.g. BLDT12345678"
                  />
                </div>
                <div>
                  <Label>Courier *</Label>
                  <Input
                    data-testid="manual-awb-courier-input"
                    value={manualAwbForm.courier_name}
                    onChange={(e) => setManualAwbForm({ ...manualAwbForm, courier_name: e.target.value })}
                    placeholder="Bluedart / DTDC / India Post"
                  />
                </div>
              </div>
              <div>
                <Label>Tracking URL</Label>
                <Input
                  data-testid="manual-awb-tracking-url-input"
                  value={manualAwbForm.tracking_url}
                  onChange={(e) => setManualAwbForm({ ...manualAwbForm, tracking_url: e.target.value })}
                  placeholder="https://… (optional)"
                />
              </div>
              <div>
                <Label>Shipping cost (INR)</Label>
                <Input
                  data-testid="manual-awb-cost-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualAwbForm.shipping_cost}
                  onChange={(e) => setManualAwbForm({ ...manualAwbForm, shipping_cost: e.target.value })}
                  placeholder="optional"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setManualAwbModalOpen(false)} data-testid="manual-awb-cancel-btn">Cancel</Button>
              <Button onClick={submitManualAwb} disabled={manualAwbSaving} data-testid="manual-awb-save-btn">
                {manualAwbSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Register AWB
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Pickup-scheduling modal — opened from Manifest tab "Schedule Pickup" CTA.
            Wraps the existing PickupScheduler component with the selected
            shipment's order/awb context so it can hit /api/pickup/schedule. */}
        <Dialog open={pickupModalOpen} onOpenChange={setPickupModalOpen}>
          <DialogContent className="max-w-md" data-testid="pickup-schedule-modal">
            <DialogHeader>
              <DialogTitle>Schedule Pickup</DialogTitle>
              <DialogDescription>
                {pickupShipment ? (
                  <>AWB <span className="font-mono font-semibold">{pickupShipment.awb}</span> · {pickupShipment.courier_name || pickupShipment.provider}</>
                ) : 'Pick a date and time for the carrier to collect this parcel.'}
              </DialogDescription>
            </DialogHeader>
            {pickupShipment && (
              <>
                {/* Order summary strip — order #, customer, destination, cost.
                    Without this the modal previously rendered with empty
                    fields when the carrier name + AWB alone weren't enough
                    context to confirm "yes, this is the right shipment". */}
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs space-y-1" data-testid="pickup-modal-order-summary">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Order:</span>
                    <span className="font-medium">{pickupShipment._order?.order_number || pickupShipment.order_id?.slice(0, 8) || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">To:</span>
                    <span className="font-medium truncate ml-2">
                      {pickupShipment._order?.customer_name || 'Customer'}
                      {pickupShipment._order?.destination_pincode ? ` · ${pickupShipment._order.destination_pincode}` : ''}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Logistics cost:</span>
                    <span className="font-medium">
                      {pickupShipment.total_logistics_cost
                        ? formatPrice(pickupShipment.total_logistics_cost)
                        : '—'}
                    </span>
                  </div>
                </div>
                <PickupScheduler
                  order={{
                    id: pickupShipment.order_id,
                    awb_number: pickupShipment.awb,
                    label_url: pickupShipment.label_url,
                  }}
                  shipmentId={pickupShipment.id}
                  existingPickup={pickupShipment.pickup_scheduled_at ? {
                    pickup_date: typeof pickupShipment.pickup_scheduled_at === 'string'
                      ? pickupShipment.pickup_scheduled_at.slice(0, 10)
                      : '',
                  } : null}
                  onScheduled={async () => {
                    setPickupModalOpen(false);
                    // Refresh the pipeline so the manifest row moves to the Pickup tab
                    try {
                      const data = await apiCall('/api/shipping/vendor/pipeline-summary');
                      setPipeline(data || { counts: {}, totals: {} });
                    } catch {}
                  }}
                />
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Pack & Ship Modal */}
        <Dialog open={packShipModalOpen} onOpenChange={setPackShipModalOpen}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="pack-ship-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Box className="h-5 w-5" />
                Pack & Ship - Order #{selectedOrder?.order_number}
              </DialogTitle>
              <DialogDescription>
                Measure the final packed box and enter dimensions below
              </DialogDescription>
            </DialogHeader>
            
            {selectedOrder && (
              <div className="space-y-4">
                {/* Order Summary */}
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span>Destination: <strong>{selectedOrder.destination_pincode}</strong></span>
                    <span>Value: <strong>{formatPrice(selectedOrder.total_amount || 0)}</strong></span>
                  </div>
                </div>
                
                {/* Dimensions Input */}
                <div className="space-y-3">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5 text-sm">
                      <Weight className="h-4 w-4" />
                      Weight (kg) *
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.05"
                      placeholder="e.g., 0.5"
                      value={packingDetails.weight_kg}
                      onChange={(e) => setPackingDetails({ ...packingDetails, weight_kg: e.target.value })}
                      data-testid="pack-weight-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Min 50g (0.05 kg)</p>
                  </div>
                  
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5 text-sm">
                      <Ruler className="h-4 w-4" />
                      Dimensions (cm) *
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Input type="number" placeholder="L" value={packingDetails.length_cm} data-testid="pack-length-input"
                        onChange={(e) => setPackingDetails({ ...packingDetails, length_cm: e.target.value })} />
                      <Input type="number" placeholder="B" value={packingDetails.breadth_cm} data-testid="pack-breadth-input"
                        onChange={(e) => setPackingDetails({ ...packingDetails, breadth_cm: e.target.value })} />
                      <Input type="number" placeholder="H" value={packingDetails.height_cm} data-testid="pack-height-input"
                        onChange={(e) => setPackingDetails({ ...packingDetails, height_cm: e.target.value })} />
                    </div>
                    {/* Volumetric Budget Meter — collapses to a single strip; auto-expands on warning */}
                    <VolumetricBudgetMeter
                      length={packingDetails.length_cm}
                      width={packingDetails.breadth_cm}
                      height={packingDetails.height_cm}
                      weight={packingDetails.weight_kg}
                      mode="fulfilment"
                      dataTestIdPrefix="pack-volumetric"
                    />
                  </div>
                  
                  {/* Insurance */}
                  <div className="flex items-center justify-between p-2.5 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-600" />
                      <div>
                        <div className="font-medium text-xs">Secure this shipment</div>
                        <div className="text-xs text-gray-500">
                          {selectedOrder.total_amount > 5000 ? 'Recommended for orders > 5,000' : 'Optional'}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={packingDetails.insurance_opt_in}
                      onCheckedChange={(checked) => setPackingDetails({ ...packingDetails, insurance_opt_in: checked })}
                    />
                  </div>
                </div>
                
                {/* Preview Button */}
                <Button onClick={previewRate} disabled={previewLoading} variant="outline" className="w-full" data-testid="calc-shipping-btn">
                  {previewLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Calculate Shipping Cost
                </Button>
                
                {/* Rate Preview */}
                {ratePreview && (
                  <div className="border rounded-lg p-3 space-y-3" data-testid="rate-preview">
                    <h4 className="font-medium flex items-center gap-2 text-sm">
                      <Truck className="h-4 w-4" />
                      Shipping Estimate
                      {ratePreview.rate_source === 'delhivery_live' && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Live Rate</span>
                      )}
                    </h4>
                    
                    {/* Route info */}
                    {ratePreview.route && (
                      <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded flex flex-wrap gap-x-3 gap-y-1">
                        <span>Origin: <strong>{ratePreview.route.origin_pincode}</strong></span>
                        <span>Dest: <strong>{ratePreview.route.destination_pincode}</strong></span>
                        <span>Zone: <strong>{ratePreview.route.zone}</strong></span>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-gray-50 p-2 rounded">
                        <div className="text-gray-500 text-xs">Provider</div>
                        <div className="font-medium text-sm">{ratePreview.best_rate?.provider}</div>
                      </div>
                      <div className="bg-gray-50 p-2 rounded">
                        <div className="text-gray-500 text-xs">Billable Weight</div>
                        <div className="font-medium text-sm">{ratePreview.dimensions?.billable_weight_kg} kg</div>
                      </div>
                      {ratePreview.dimensions?.volumetric_weight_kg > ratePreview.dimensions?.weight_kg && (
                        <div className="col-span-2 bg-amber-50 text-amber-700 p-2 rounded text-xs">
                          Volumetric weight ({ratePreview.dimensions.volumetric_weight_kg} kg) exceeds actual weight ({ratePreview.dimensions.weight_kg} kg). Billed at volumetric.
                        </div>
                      )}
                    </div>
                    
                    {/* Weight mismatch warning */}
                    {ratePreview.weight_mismatch && (
                      <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-semibold">Weight Mismatch Detected</div>
                          <div>{ratePreview.weight_mismatch_message}</div>
                        </div>
                      </div>
                    )}
                    
                    <div className="border-t pt-2 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Actual Shipping Cost</span>
                        <span>{formatPrice(ratePreview.best_rate?.base_cost)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500 text-xs">
                        <span>GST (18%)</span>
                        <span>{formatPrice(ratePreview.best_rate?.gst)}</span>
                      </div>
                      {ratePreview.customer_charged > 0 && (
                        <div className="flex justify-between text-gray-500 text-xs">
                          <span>Customer was charged</span>
                          <span className="text-green-600">{formatPrice(ratePreview.customer_charged)}</span>
                        </div>
                      )}
                      {packingDetails.insurance_opt_in && ratePreview.insurance?.fee > 0 && (
                        <div className="flex justify-between text-gray-500 text-xs">
                          <span>Insurance</span>
                          <span>{formatPrice(ratePreview.insurance.fee)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold pt-2 border-t text-sm">
                        <span>Total Deduction</span>
                        <span className="text-orange-600">
                          {formatPrice(
                            (ratePreview.best_rate?.total_cost || 0) + 
                            (packingDetails.insurance_opt_in ? (ratePreview.insurance?.fee || 0) : 0)
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Save dimensions prompt (single product only) */}
                    {ratePreview.can_save_dimensions && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded p-2.5 flex items-center justify-between gap-2">
                        <div className="text-xs text-indigo-700">
                          <div className="font-medium">Save these dimensions to the listing?</div>
                          <div className="text-indigo-500">Future shipping quotes will use this weight & size.</div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0 text-xs h-7 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                          onClick={saveDimensionsToListing}
                          disabled={savingDimensions}
                          data-testid="save-dimensions-btn"
                        >
                          {savingDimensions ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      This amount will be deducted from your payout
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              {!awbResult ? (
                <>
                  <Button variant="outline" onClick={() => setPackShipModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={generateAWB} disabled={!ratePreview || generatingLabel} data-testid="generate-awb-btn">
                    {generatingLabel ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
                    Generate AWB
                  </Button>
                </>
              ) : (
                <>
                  {/* AWB Generated — show AWB info + Download Label CTA */}
                  <div className="w-full bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-semibold text-green-800 text-sm">AWB Generated Successfully</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">AWB Number</span>
                        <p className="font-mono font-bold text-gray-900" data-testid="awb-number">{awbResult.awb}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Provider</span>
                        <p className="font-medium text-gray-900">{awbResult.courier || awbResult.provider}</p>
                      </div>
                      {awbResult.estimated_cost > 0 && (
                        <div>
                          <span className="text-gray-500">Shipping Cost</span>
                          <p className="font-medium text-gray-900">{formatPrice(awbResult.total_logistics_cost || awbResult.estimated_cost)}</p>
                        </div>
                      )}
                      {awbResult.estimated_delivery_days && (
                        <div>
                          <span className="text-gray-500">Est. Delivery</span>
                          <p className="font-medium text-gray-900">{awbResult.estimated_delivery_days} days</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 w-full">
                    <Button variant="outline" onClick={() => { setPackShipModalOpen(false); fetchData(); }} className="flex-1">
                      Close
                    </Button>
                    <Button 
                      onClick={downloadLabel} 
                      disabled={labelDownloading || !awbResult.label_url} 
                      className="flex-1"
                      data-testid="download-label-btn"
                    >
                      {labelDownloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                      Download Shipping Label
                    </Button>
                  </div>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Tracking Dialog */}
        <Dialog open={trackingDialogOpen} onOpenChange={setTrackingDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Navigation className="h-5 w-5" />
                Shipment Tracking
              </DialogTitle>
              {trackingData?.awb && (
                <DialogDescription>AWB: {trackingData.awb} | Provider: {trackingData.provider}</DialogDescription>
              )}
            </DialogHeader>
            
            {trackingLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : !trackingData?.has_shipment && trackingData?.has_shipment !== undefined ? (
              <div className="py-6 text-center text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{trackingData?.message || 'No shipment found'}</p>
              </div>
            ) : trackingData?.events?.length > 0 ? (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                <div className="mb-3">
                  <Badge className={
                    trackingData.current_status === 'delivered' ? 'bg-green-100 text-green-700' :
                    trackingData.current_status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }>
                    {trackingData.current_status?.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                </div>
                {trackingData.events.map((event, idx) => (
                  <div key={event.timestamp || `event-${idx}`} className="flex gap-3 py-2 border-l-2 border-gray-200 pl-4 relative">
                    <div className="absolute -left-1.5 top-3 w-3 h-3 rounded-full bg-white border-2 border-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{event.status?.replace(/_/g, ' ')}</p>
                      {event.description && <p className="text-xs text-gray-600">{event.description}</p>}
                      <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                        {event.location && <span>{event.location}</span>}
                        {event.timestamp && <span>{new Date(event.timestamp).toLocaleString()}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-gray-500">
                <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No tracking events yet</p>
                {trackingData?.live_error && (
                  <p className="text-xs text-red-400 mt-2">Live tracking unavailable: {trackingData.live_error}</p>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setTrackingDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PackAndShip;
